import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Table, Button, Form, DropdownButton, Dropdown, Container } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function Training() {
  const { token } = useContext(AuthContext);
  const { selectedStore, stores, setSelectedStore, fetchStores } = useContext(StoreContext);
  const [trainingData, setTrainingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newTraining, setNewTraining] = useState({ intent: '', response: '', type: 'faq' });

  const [typeFilter, setTypeFilter] = useState('');
  const [typeFilterTitle, setTypeFilterTitle] = useState('All Types');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (token) {
      fetchStores();
      if (selectedStore) {
        fetchTrainingData(selectedStore, typeFilter, 1);
      }
    }
  }, [token, selectedStore, typeFilter]);

  const fetchTrainingData = async (storeId, filter, pageNumber) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${STORE_API_URL}/training/${storeId}?type=${filter}&page=${pageNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error('Error fetching training data.');
      }
      const data = await response.json();

      if (pageNumber === 1) {
        setTrainingData(data.trainingData);
      } else {
        setTrainingData((prev) => [...prev, ...data.trainingData]);
      }
      setHasMore(data.hasMore);
    } catch (error) {
      setError(error.message || 'An error occurred while fetching training data.');
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTrainingData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTrainingData(selectedStore, typeFilter, nextPage);
  };

  const handleSave = async (training) => {
    const method = training._id ? 'PUT' : 'POST';
    const endpoint = training._id
      ? `${STORE_API_URL}/training/${training._id}`
      : `${STORE_API_URL}/training`;

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...training, store_id: selectedStore }),
      });

      if (!response.ok) throw new Error('Failed to save training data.');
      toast.success(training._id ? 'Training data updated!' : 'New training data added!');
      fetchTrainingData(selectedStore, typeFilter, 1);
      setEditingId(null);
      setNewTraining({ intent: '', response: '', type: 'faq' });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this training data?')) return;
    try {
      await fetch(`${STORE_API_URL}/training/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Training data deleted!');
      fetchTrainingData(selectedStore, typeFilter, 1);
    } catch (error) {
      toast.error('Failed to delete training data.');
    }
  };

  const handleTypeChange = (filter, title) => {
    setTypeFilter(filter);
    setTypeFilterTitle(title);
    setPage(1);
  };

  const handleStoreChange = (e) => {
    const newStoreId = e.target.value;
    setSelectedStore(newStoreId);
    localStorage.setItem('selectedStore', newStoreId);
  };

  return (
    <Container>
      <ToastContainer />

      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Training Data</h2>
        {selectedStore && stores.length > 0 && (
          <div className="form-group">
            <select id="storeSelect" className="form-select" value={selectedStore || ''} onChange={handleStoreChange}>
              {stores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="filter-buttons mb-3">
        <DropdownButton id="type-filter" title={typeFilterTitle} align="end">
          <Dropdown.Item onClick={() => handleTypeChange('', 'All Types')}>All Types</Dropdown.Item>
          <Dropdown.Item onClick={() => handleTypeChange('faq', 'FAQ')}>FAQ</Dropdown.Item>
          <Dropdown.Item onClick={() => handleTypeChange('greeting', 'Greeting')}>Greeting</Dropdown.Item>
          <Dropdown.Item onClick={() => handleTypeChange('order_status', 'Order Status')}>Order Status</Dropdown.Item>
        </DropdownButton>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && trainingData.length > 0 && (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Intent</th>
              <th>Response</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><Form.Control as="textarea" value={newTraining.intent} onChange={(e) => setNewTraining({ ...newTraining, intent: e.target.value })} /></td>
              <td><Form.Control as="textarea" value={newTraining.response} onChange={(e) => setNewTraining({ ...newTraining, response: e.target.value })} /></td>
              <td>
                <Form.Select value={newTraining.type} onChange={(e) => setNewTraining({ ...newTraining, type: e.target.value })}>
                  <option value="faq">FAQ</option>
                  <option value="greeting">Greeting</option>
                  <option value="order_status">Order Status</option>
                  <option value="custom">Custom</option>
                </Form.Select>
              </td>
              <td>
                <Button variant="success" onClick={() => handleSave(newTraining)}><FaPlus /></Button>
              </td>
            </tr>
            {trainingData.map((training) => (
              <tr key={training._id}>
                <td>
                  {editingId === training._id ? (
                    <Form.Control as="textarea" value={training.intent} onChange={(e) => setTrainingData(trainingData.map(t => t._id === training._id ? { ...t, intent: e.target.value } : t))} />
                  ) : (
                    training.intent
                  )}
                </td>
                <td>
                  {editingId === training._id ? (
                    <Form.Control as="textarea" value={training.response} onChange={(e) => setTrainingData(trainingData.map(t => t._id === training._id ? { ...t, response: e.target.value } : t))} />
                  ) : (
                    training.response
                  )}
                </td>
                <td>
                  {editingId === training._id ? (
                    <Form.Select value={training.type} onChange={(e) => setTrainingData(trainingData.map(t => t._id === training._id ? { ...t, type: e.target.value } : t))}>
                      <option value="faq">FAQ</option>
                      <option value="greeting">Greeting</option>
                      <option value="order_status">Order Status</option>
                      <option value="custom">Custom</option>
                    </Form.Select>
                  ) : (
                    training.type
                  )}
                </td>
                <td>
                  {editingId === training._id ? (
                    <>
                      <Button variant="success" onClick={() => handleSave(training)}><FaCheck /></Button>{' '}
                      <Button variant="secondary" onClick={() => setEditingId(null)}><FaTimes /></Button>
                    </>
                  ) : (
                    <>
                      <Button variant="warning" onClick={() => setEditingId(training._id)}><FaEdit /></Button>{' '}
                      <Button variant="danger" onClick={() => handleDelete(training._id)}><FaTrash /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {hasMore && !loading && (
        <div className="text-center mt-3">
          <Button onClick={loadMoreTrainingData}>Load More</Button>
        </div>
      )}
    </Container>
  );
}

export default Training;
